import React, { useState } from "react";
import CollapsibleTypographyCanvasPopup from "../generics/components/collapsible-typography-canvas-popup";
import MaterialQuoteViewCanvasPopup from "./material-quoteview-canvas-popup";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  ListItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { baseAtom, selectInterviewWithInsight } from "../../stores/jotai/base";
import { useAtom, useAtomValue } from "jotai";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { interviewsAtom } from "../../stores/jotai/interviews/interviews";
import * as commentUtilities from "../../stores/cohort/utils";
import MaterialAvatar from "../general/MaterialAvatar";
import { aiIndicationIconsAvatar } from "../../../lib/teacup/lib/ai-indicator";
import ThumbsUpIcon from "../../../icons/ThumbsUpIcon";
import ThumbsDownIcon from "../../../icons/ThumbsDownIcon";
import useDashJSPlayer from "../../hooks/useDashJSPlayer";
import PlayAudioIcon from "../../../icons/PlayAudioIcon";
import PauseAudioIcon from "../../../icons/PauseAudioIcon";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useGetMetaArtifacts } from "../../tanstackQuery/queries/artifacts";
import { useKeycloak } from "@react-keycloak/web";
import AnalysisInsightCards from "../discovery/analysis/analysis-insights-details";
import {
  getQuote,
  getQuoteDescription,
} from "../../util/insights/handle-export";
import { extractInsightGroup } from "../insights/insight-details";

const GOOGLE_SPACE_BORDER_COLOR = "#DDDDDD";
const dateFormatter = "ddd, DD MMM YYYY";

const tooltipZIndexIcon = {
  popper: {
    style: {
      zIndex: 1400,
    },
  },
};

const InsightDialogItem = ({
  finding,
  updatePopupHeight,
  handleProcessEscaping,
  findingLinksUser,
}) => {
  const [base, setBase] = useAtom(baseAtom);
  const navigate = useNavigate();
  const interviews = useAtomValue(interviewsAtom);
  const theme = useTheme();
  const { dashJSPlayer } = useDashJSPlayer();
  const { keycloak } = useKeycloak();

  const [playedFindingId, setPlayedFindingId] = useState<string>("");
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {}
  );
  const [matchedInsightGroup, setMatchedInsightGroup] =
    useState<any>(undefined);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);

  const { org: orgData, members, cohort, team } = base;
  const org = {
    ...orgData,
    members: members,
  };

  const {
    refetch: refetchMetaArtifacts,
    isFetching,
    data: metaArtifactData,
    isFetched,
  } = useGetMetaArtifacts(
    { accessToken: keycloak.token },
    { artifactID: finding?.artifactID, isMappedByParent: true },
    {
      enabled: false,
      // retry: 1,
      // retryDelay: 3000,
    }
  );

  const goToFinding = (finding: api.FindingResponse) => {
    if (
      finding &&
      finding?.interviewID &&
      finding?.artifactID &&
      finding?.findingID
    ) {
      const url = `/discovery/${finding?.interviewID}/${finding?.artifactID}/${finding?.findingID}`;
      selectInterviewWithInsight(
        setBase,
        finding?.interviewID,
        finding,
        interviews
      );
      navigate(url);
      handleProcessEscaping();
    }
  };

  const onPlayInsightAudio = (finding) => {
    if (
      playedFindingId === finding?.findingID &&
      !dashJSPlayer?.playBackCompleted
    ) {
      dashJSPlayer?.instance.play();
    } else {
      setPlayedFindingId(finding.findingID);
      dashJSPlayer.loadMediaByInterviewID(
        finding?.interviewID,
        parseFloat(finding?.audioBegin)
      );

      dashJSPlayer.play(null, parseFloat(finding?.audioEnd));
    }
  };

  if (finding === undefined) {
    return <div></div>;
  }

  let quoteValue;
  try {
    quoteValue = JSON.parse(finding?.quote);
  } catch (e) {
    quoteValue = null;
  }

  const findingUserID = finding?.userID;

  const findingComment = (
    <CollapsibleTypographyCanvasPopup
      quote={finding?.comment}
      fontStyle={"normal"}
      quoteDecoration={""}
      quouteCollapseLength={250}
      isComment={true}
      updatePopupHeight={updatePopupHeight}
    />
  );

  const findingQuote = (
    <MaterialQuoteViewCanvasPopup
      artifactID={finding?.artifactID}
      selection={finding?.quote}
      quouteCollapseLength={250}
      routeToInsight={() => {
        goToFinding(finding);
      }}
      updatePopupHeight={updatePopupHeight}
    />
  );

  const hasFindingComment = finding.comment !== "";

  const renderValidationIcon = (finding: api.FindingResponse) => {
    let iconColor = "#FFFFFF";

    if (finding?.supports === 1 || finding?.supports === true) {
      iconColor = theme?.custom?.validInsightGreen;

      return (
        <Tooltip
          title="Supporting evidence"
          slotProps={{ ...tooltipZIndexIcon }}
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <ThumbsUpIcon
              sx={{ marginRight: "4px", fill: iconColor }}
              htmlColor={iconColor}
            />
          </Box>
        </Tooltip>
      );
    } else {
      iconColor = theme?.custom?.invalidInsightRed;
      return (
        <Tooltip
          title="Non-supporting evidence"
          slotProps={{ ...tooltipZIndexIcon }}
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <ThumbsDownIcon
              sx={{
                marginRight: "4px",
                fill: iconColor,
                transform: "scaleX(-1)",
              }}
              htmlColor={iconColor}
            />
          </Box>
        </Tooltip>
      );
    }
  };

  const renderUserAvatar = (user, finding) => {
    const userRoleColor = commentUtilities.findUserRoleColor(
      user?.userID,
      org,
      team,
      cohort
    );

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <MaterialAvatar
          user={user}
          sx={{
            width: 48,
            height: 48,
            fontSize: 16,
            // marginRight: "6px",
          }}
          roleColor={userRoleColor}
        />
        {finding?.findingInsertionType && (
          <Stack
            className="canvas-badge-bar ai-indicator-badge"
            direction="row"
            // spacing={1.25}
            alignItems="center"
            sx={{
              backgroundColor: theme?.custom?.white,
              padding: "1.5px 6px",
              borderRadius: "15px",
              border: "1px solid rgb(130,130,130)",
              position: "absolute",
              bottom: -7,
            }}
          >
            <Stack direction="row" spacing={0.15} alignItems="center">
              {aiIndicationIconsAvatar?.[finding?.findingInsertionType]}
            </Stack>
          </Stack>
        )}
      </div>
    );
  };

  return (
    <>
    {isLoadingMeta && <LinearProgress />}
    <ListItem
      id={"fnd-" + finding?.findingID}
      sx={{ padding: "0", width: "100%" }}
    >
      <Stack
        sx={{ width: "100%", padding: "8px 5px" }}
        direction="column"
        spacing={0}
      >
        
        <Stack direction="row" spacing={0} alignItems="center">
          {renderUserAvatar(findingLinksUser?.[findingUserID], finding)}
          <Stack
            sx={{ flexGrow: "1", margin: "0px 0px 0px 6px" }}
            direction="column"
            spacing={0}
          >
            <Typography sx={{ fontSize: "14px", lineHeight: "18px" }}>
              {commentUtilities.findUserName(findingUserID, org)}
            </Typography>
            <Tooltip title={finding?.interviewee?.length>30 ? finding?.interviewee:""} slotProps={{ ...tooltipZIndexIcon }}>
             <Typography
              noWrap
              fontWeight={400}
              sx={{ fontSize: "14px", lineHeight: "18px",maxWidth: 220 }}
            >
              {finding?.interviewee}
            </Typography>
            </Tooltip>
            <Typography
              fontWeight={400}
              sx={{
                fontSize: "12px",
                lineHeight: "14px",
                color: theme?.custom?.mainIconColor,
              }}
            >
              {moment(finding?.created).format(dateFormatter)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
            {renderValidationIcon(finding)}

            {!!finding?.audioBegin && !!finding?.audioEnd && (
              <>
                {!dashJSPlayer?.isPaused &&
                playedFindingId === finding.findingID ? (
                  <IconButton
                    onClick={() => {
                      dashJSPlayer?.instance.pause();
                      setPlayedFindingId(finding?.findingID);
                    }}
                  >
                    <Tooltip
                      title="Pause audio"
                      slotProps={{ ...tooltipZIndexIcon }}
                    >
                      <PauseAudioIcon />
                    </Tooltip>
                  </IconButton>
                ) : (
                  <IconButton onClick={() => onPlayInsightAudio(finding)}>
                    <Tooltip
                      title="Play audio"
                      slotProps={{ ...tooltipZIndexIcon }}
                    >
                      <PlayAudioIcon />
                    </Tooltip>
                  </IconButton>
                )}
              </>
            )}
            <Tooltip
              title="View in the Interview Section"
              slotProps={{ ...tooltipZIndexIcon }}
            >
              <IconButton
                className="clickSafe"
                onClick={() => {
                  goToFinding(finding);
                }}
              >
                <OpenInNewRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack>
          <Box
            sx={{
              padding: "8px 0px",
            }}
          >
            <Box
              sx={
                quoteValue?.source === "note" ||
                quoteValue?.source === "transcript"
                  ? {
                      cursor: "pointer",
                      color: theme?.custom?.mainButtonHighlight,
                      textDecoration: "underline",
                    }
                  : undefined
              }
            >
              {quoteValue?.source === "note" ||
              quoteValue?.source === "transcript" ? (
                <>{findingQuote}</>
              ) : (
                <Box sx={{mt:1}}>
                  <AnalysisInsightCards
                    key={`${finding?.findingID}-canvas`}
                    item={
                      matchedInsightGroup 
                        ? matchedInsightGroup
                        : {
                            title: getQuote(finding?.quote),
                            description: getQuoteDescription(finding?.quote),
                            insights: [],
                          }
                    }
                    showInsights={true}
                    highlightCard={true}
                    collapsible={true}
                    onExpand={async ()=>{
                      if (!matchedInsightGroup) {
                        setIsLoadingMeta(true);
                        const result = await refetchMetaArtifacts();
                        setIsLoadingMeta(false);
                        const newMeta = result?.data;

                        if (newMeta) {
                          const found = extractInsightGroup(finding.findingID, newMeta);
                          if (found) {
                            setMatchedInsightGroup(found);
                          }
                        }
                      }
                    }}
                    isSelectable={false}
                    headingStyle={{
                      fontSize:"15px"
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          {hasFindingComment ? (
            <Box
              sx={{
                padding: "8px 0px",
              }}
            >
              {findingComment}
            </Box>
          ) : null}
          <Divider
            sx={{
              width: "70%",
              margin: "6px auto",
              borderColor: GOOGLE_SPACE_BORDER_COLOR,
            }}
          />
        </Stack>
      </Stack>
    </ListItem>
    </>
  );
};

export default InsightDialogItem;
